import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { planningApi } from '../../api/availability.ts';
import { scheduleApi } from '../../api/endpoints.ts';
import { Button, ConfirmDialog, LoadingBlock } from '../ui.tsx';
import type { PlanningConflict } from '../../../shared/types-availability.ts';
import { formatLocalTime, formatNumericDate } from '../../i18n/datetime.ts';
export function PlanningConflictActions({ row, enabled }: { row: PlanningConflict; enabled: boolean }) {
  const { t } = useTranslation();
  const queries = useQueryClient();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const picks = useQuery({
    queryKey: ['planning-move', row.key],
    queryFn: () => planningApi.moveSuggestions(row.appointmentId),
    enabled: open,
    retry: false,
  });
  async function write(work: () => Promise<unknown>) {
    setBusy(true);
    setError('');
    try {
      await work();
      await queries.invalidateQueries({
        predicate: (q) => String(q.queryKey[0]).startsWith('planning-') || q.queryKey[0] === 'appointments',
      });
      setOpen(false);
      setSelected(null);
    } catch (error) {
      setError(error instanceof Error ? error.message : t('planning.failed'));
    } finally {
      setBusy(false);
    }
  }
  const pick = selected === null ? null : picks.data?.suggestions[selected];
  return (
    <>
      <div className="mt-2 flex flex-wrap gap-2">
        <Button
          disabled={!enabled || busy}
          onClick={() => {
            setOpen(true);
            setError('');
          }}
        >
          {t('planning.move')}
        </Button>
        <Link className="btn btn-secondary" to={`/schedule?appointment=${row.appointmentId}`}>
          {t('planning.reassign')}
        </Link>
        <Button
          variant="secondary"
          disabled={!enabled || busy}
          onClick={() => void write(() => planningApi.ignore(row.key))}
        >
          {t('planning.ignore')}
        </Button>
      </div>
      {error && !open && (
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
      )}
      {open && (
        <ConfirmDialog
          title={t('planning.move')}
          confirmLabel={t('planning.move')}
          cancelLabel={t('planning.cancel')}
          confirmDisabled={!pick || !enabled}
          busy={busy}
          error={error}
          onCancel={() => {
            setOpen(false);
            setSelected(null);
          }}
          onConfirm={() => {
            if (pick)
              void write(() =>
                scheduleApi.reschedule(row.appointmentId, {
                  scheduledDate: pick.startsOn,
                  startTimeLocal: pick.startTimeLocal,
                }),
              );
          }}
        >
          <p className="mb-3">{t('planning.sameCrew')}</p>
          {picks.isPending && <LoadingBlock />}
          {picks.isError && <Button onClick={() => void picks.refetch()}>{t('planning.retry')}</Button>}
          {picks.data?.warning && <p role="status">{t('planning.uncertain')}</p>}
          {picks.data?.suggestions.length === 0 && <p>{t('planning.noPicks')}</p>}
          <fieldset className="space-y-2">
            <legend className="sr-only">{t('planning.sameCrew')}</legend>
            {picks.data?.suggestions.map((pick, index) => (
              <label
                key={`${pick.startsOn}-${pick.startTimeLocal}`}
                className="flex items-center gap-2 rounded border p-2"
              >
                <input
                  type="radio"
                  name={`planning-move-${row.key}`}
                  checked={selected === index}
                  onChange={() => setSelected(index)}
                />
                <span>
                  {formatNumericDate(pick.startsOn)} · {formatLocalTime(pick.startTimeLocal)}
                </span>
              </label>
            ))}
          </fieldset>
        </ConfirmDialog>
      )}
    </>
  );
}
